import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './cookie-preferences.module.scss';
import Modal from '@/components/Modal/modal';
import Button from '@/components/Button';
import useConsent from '../../hooks/useConsent';
import { ConsentCategories } from '../../model';
import { toCapitalCase } from '../../functions/string';

const CookiePreferences = () => {
  const { consent, updateCategories, prefsOpen, closePrefs } = useConsent();
  const [formCategories, setFormCategories] = useState<
    ConsentCategories | undefined
  >();

  useEffect(() => {
    if (prefsOpen) {
      setFormCategories(
        consent.given
          ? {
              necessary: true,
              analytics: consent.categories.analytics,
            }
          : {
              necessary: true,
              analytics: true,
            }
      );
    }
  }, [consent.categories, consent.given, prefsOpen]);

  if (!prefsOpen) return null;
  return createPortal(
    <Modal
      title="Cookie Preferences Settings"
      isOpen={prefsOpen}
      onClose={closePrefs}
    >
      <div className={styles['cookie-preferences']}>
        <p>Choose which types of cookies you'd like to allow:</p>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {typeof formCategories !== 'undefined' &&
              Object.keys(formCategories).map((key) => {
                return (
                  <tr className={styles['cookie-preferences__item']} key={key}>
                    <td>
                      {['allow', 'disallow'].map((action) => {
                        const label = toCapitalCase(action);
                        const active =
                          typeof formCategories[
                            key as keyof ConsentCategories
                          ] === 'undefined'
                            ? false
                            : action === 'allow'
                              ? formCategories[key as keyof ConsentCategories]
                              : !formCategories[key as keyof ConsentCategories];
                        return (
                          <button
                            key={label}
                            title={`${label} cookies`}
                            className={styles['cookie-preferences__item__btn']}
                            data-active={active}
                            onClick={() => {
                              if (active) return;

                              setFormCategories((prev) => {
                                if (typeof prev === 'undefined') return prev;
                                return {
                                  ...prev,
                                  [key]: action === 'allow' ? true : false,
                                };
                              });
                            }}
                            disabled={
                              key === 'necessary' // necessary cookies cannot be disabled
                            }
                          >
                            {label}
                          </button>
                        );
                      })}
                    </td>
                    <td className={styles['cookie-preferences__item']}>
                      <p>
                        <strong>{toCapitalCase(key)}</strong>
                        {key === 'necessary' && (
                          <>
                            <span>[ALWAYS ACTIVE]</span>Essential cookies
                            required for the website to function properly.
                          </>
                        )}
                        {key === 'analytics' && (
                          <>
                            Helps us understand how visitors use our site
                            through <span>Google Analytics</span> and{' '}
                            <span>Microsoft Clarity</span>, so we can improve
                            your experience.
                          </>
                        )}
                      </p>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <p>
          You can update these preferences anytime in our{' '}
          <a href="/cookie-settings" target="_blank">
            Cookie Settings
          </a>{' '}
          page. Questions? Contact us at{' '}
          <a href="mailto:info@gannet.ai&subject=Dashboard%20Suggestion">
            info@gannet.ai
          </a>
          .
        </p>
        <hr />
        <Button
          variation="secondary"
          className={styles['cookie-preferences__save-btn']}
          onClick={() => {
            if (typeof formCategories !== 'undefined') {
              updateCategories(formCategories, true);
              closePrefs();
            }
          }}
        >
          Save Preferences
        </Button>
      </div>
    </Modal>,
    document.body
  );
};

export default CookiePreferences;
